import type { PhoneInput } from 'application/ui/fields/phone/types'
import { validateForm, showError } from 'application/utils/form-validation'

/**
 * Класс для управления формой обратной связи на странице контактов.
 * Обрабатывает отправку формы, валидацию и отображение результатов.
 */
export class ContactsForm {
  private container: HTMLElement
  private form: HTMLFormElement
  private info: HTMLElement | null
  private success: HTMLElement | null

  /**
   * Создает экземпляр формы контактов.
   * @param {HTMLElement} container - Контейнер с формой контактов
   */
  constructor(container: HTMLElement) {
    this.container = container
    this.init()
  }

  /**
   * Инициализирует форму: находит необходимые элементы и устанавливает обработчики событий.
   * Привязывает обработчик отправки формы и кнопки возврата к форме после успешной отправки.
   * @private
   */
  private init() {
    this.form = this.container.querySelector<HTMLFormElement>(
      '[data-contacts-target="form"]'
    )!

    if (!this.form) return

    this.info = this.container.querySelector<HTMLElement>(
      '[data-contacts-target="info"]'
    )

    this.success = this.container.querySelector<HTMLElement>(
      '[data-contacts-target="success"]'
    )

    this.form.addEventListener('submit', this.onSubmit)

    const returnBtn = this.success?.querySelector<HTMLButtonElement>(
      '[data-action="return"]'
    )

    returnBtn?.addEventListener('click', () => {
      this.hideSuccess()
    })
  }

  /**
   * Обработчик отправки формы.
   * Предотвращает стандартное поведение, выполняет валидацию,
   * отправляет данные на сервер и отображает результат.
   * @param {Event} event - Событие отправки формы
   * @private
   */
  private onSubmit = async (event: Event) => {
    event.preventDefault()
    // Handle form submission logic here
    const form = event.target as HTMLFormElement

    if (!validateForm(form)) {
      return
    }

    this.fetchForm(form).then((success) => {
      if (success) {
        this.showSuccess()

        form.reset()
      }
    })
  }

  /**
   * Отправляет данные формы на сервер.
   * Обрабатывает форматирование телефона, отправку через fetch API,
   * управление состоянием загрузки кнопки и обработку ошибок.
   * @param {HTMLFormElement} form - HTML-форма для отправки
   * @return {Promise<boolean>} Promise с результатом отправки (true - успех, false - ошибка)
   * @private
   */
  private async fetchForm(form: HTMLFormElement): Promise<boolean> {
    const submitBtn = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!

    try {
      const formData = new FormData(form)

      const action = form.getAttribute('action')
      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]'
      )?.content
      submitBtn.classList.add('loading')

      const response = await fetch(action || '', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        },
        body: formData
      })

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      if (response.status !== 200) {
        throw new Error('Ошибка при отправке формы')
      }
    } catch (error) {
      showError(form, (error as Error).message)
      return false
    } finally {
      submitBtn.classList.remove('loading')
    }

    return true
  }

  /**
   * Отображает экран успешной отправки формы.
   * Скрывает информационный блок и форму, показывает сообщение об успехе.
   * @private
   */
  private showSuccess() {
    this.info?.classList.add('is-hidden')
    this.form.classList.add('is-hidden')
    this.success?.classList.remove('is-hidden')
  }

  /**
   * Скрывает экран успешной отправки и возвращает пользователя к форме.
   * Показывает информационный блок и форму, скрывает сообщение об успехе.
   * @private
   */
  private hideSuccess() {
    this.info?.classList.remove('is-hidden')
    this.form.classList.remove('is-hidden')
    this.success?.classList.add('is-hidden')
  }
}
